/**
 * Customer side-navigation configuration — the single source of truth for the
 * sectioned, foldable sidebar (`SideNav`).
 *
 * The whole rail is generated from `NAV_GROUPS`: a list of named sections, each
 * carrying a category glyph (so the user can scan by shape) and a list of items
 * (route + icon + i18n label). Adding a page or a section is a one-line edit
 * here — the headers, counts, fold logic and active-matching all follow.
 *
 * Brand: Deep Navy ground, white rest glyphs/labels, Signal Green active fill
 * with a Cargo Black glyph/label (selection accent). Category glyphs are the
 * house single-stroke line set; some recolour via `fill`, the Devices/GritClaw
 * glyphs via `currentColor` stroke — encoded per-item as `paint`.
 */
import React from 'react';

// ── Category (section) glyphs — scan-by-shape markers ───────────────────────
import { ReactComponent as MonitorGlyph } from '../../assets/icons/signal.svg';
import { ReactComponent as OperationsGlyph } from '../../assets/icons/route.svg';
import { ReactComponent as IntelligenceGlyph } from '../../assets/icons/rocket.svg';
import { ReactComponent as HardwareGlyph } from '../../assets/icons/devices-dark.svg';
import { ReactComponent as AdminGlyph } from '../../assets/icons/gear-dark.svg';

// ── Per-item glyphs ─────────────────────────────────────────────────────────
import { ReactComponent as MapIcon } from '../../assets/icons/map-dark.svg';
import { ReactComponent as AlertsIcon } from '../../assets/icons/rtl-alerts-dark.svg';
import { ReactComponent as ShipmentsIcon } from '../../assets/icons/ship-dark.svg';
import { ReactComponent as LocationsIcon } from '../../assets/icons/pin-dark.svg';
import { ReactComponent as AssistantIcon } from '../../assets/icons/grit-assistant.svg';
import { ReactComponent as SchedulesIcon } from '../../assets/icons/grit-schedule.svg';
import { ReactComponent as AlertRulesIcon } from '../../assets/icons/grit-alert-rules.svg';
import { ReactComponent as ReportsIcon } from '../../assets/icons/chart-mixed.svg';
import { ReactComponent as HealthIcon } from '../../assets/icons/signal.svg';
import { ReactComponent as DevicesIcon } from '../../assets/icons/devices-dark.svg';
import { ReactComponent as ToleranceIcon } from '../../assets/icons/tolerance-profiles-dark.svg';
import { ReactComponent as UsersIcon } from '../../assets/icons/portal-user-dark.svg';
import { ReactComponent as SettingsIcon } from '../../assets/icons/settings-dark.svg';
import { ReactComponent as ResellersIcon } from '../../assets/icons/resellers-icon.svg';
import { ReactComponent as RtlIcon } from '../../assets/icons/rtl-tab.svg';

/** CRA `ReactComponent` SVG type (see src/types/declarations.d.ts). */
export type SvgIcon = React.FunctionComponent<
  React.SVGProps<SVGSVGElement> & { title?: string }
>;

/** How a glyph takes colour: most via `fill`, a few via `currentColor` stroke. */
export type Paint = 'fill' | 'stroke';

/** Role / scope facts that drive which items are visible. */
export interface NavContext {
  isSpectator: boolean;
  isReadOnly: boolean;
}

export interface NavItem {
  /** Stable key + route target. */
  id: string;
  path: string;
  /** English string — also the i18next key (the repo's convention). */
  label: string;
  Icon: SvgIcon;
  paint: Paint;
  /** Optional count badge (e.g. unread alerts). Unwired by default. */
  badge?: number;
  /** Whether this item is the active page for a given pathname. */
  isActive: (pathname: string) => boolean;
  /** Whether this item is shown for the current role / scope. */
  isVisible: (ctx: NavContext) => boolean;
}

export interface NavGroup {
  /** Stable key (drives the fold state + its persistence). */
  id: string;
  label: string;
  Icon: SvgIcon;
  paint: Paint;
  /** Marks every feature in this section as a beta experience. */
  beta?: boolean;
  items: NavItem[];
}

// ── Small predicate helpers (keep the table below declarative) ──────────────
const exact = (p: string) => (pathname: string) => pathname === p;
/** Shown to every role except a read-only SPECTATOR (who only sees the Map). */
const notSpectator = (ctx: NavContext) => !ctx.isSpectator;
const always = () => true;

/**
 * The customer rail. Five sections, balanced two-per, each with a distinct
 * category glyph. Only live (routed) pages are listed — commented-out legacy
 * routes (Seals, History, Drivers, …) are intentionally omitted.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'monitor',
    label: 'Monitor',
    Icon: MonitorGlyph,
    paint: 'fill',
    items: [
      {
        id: '/',
        path: '/',
        label: 'Map',
        Icon: MapIcon,
        paint: 'fill',
        isActive: exact('/'),
        isVisible: always,
      },
      {
        id: '/device-alerts',
        path: '/device-alerts',
        label: 'Device Alerts',
        Icon: AlertsIcon,
        paint: 'fill',
        isActive: exact('/device-alerts'),
        isVisible: notSpectator,
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    Icon: OperationsGlyph,
    paint: 'fill',
    items: [
      {
        id: '/shipments',
        path: '/shipments',
        label: 'Shipments',
        Icon: ShipmentsIcon,
        paint: 'fill',
        isActive: exact('/shipments'),
        isVisible: notSpectator,
      },
      {
        id: '/locations',
        path: '/locations',
        label: 'Locations',
        Icon: LocationsIcon,
        paint: 'fill',
        isActive: exact('/locations'),
        isVisible: notSpectator,
      },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    Icon: IntelligenceGlyph,
    paint: 'fill',
    beta: true,
    items: [
      {
        id: '/gritclaw',
        path: '/gritclaw',
        label: 'Assistant',
        Icon: AssistantIcon,
        paint: 'stroke',
        isActive: exact('/gritclaw'),
        isVisible: notSpectator,
      },
      {
        id: '/reports',
        path: '/reports',
        label: 'Reports',
        Icon: ReportsIcon,
        paint: 'fill',
        // Highlight on the list and any /reports/:id view/builder page.
        isActive: (p) => p === '/reports' || p.startsWith('/reports/'),
        isVisible: notSpectator,
      },
      {
        id: '/gritclaw/schedules',
        path: '/gritclaw/schedules',
        label: 'Schedules',
        Icon: SchedulesIcon,
        paint: 'stroke',
        isActive: exact('/gritclaw/schedules'),
        isVisible: notSpectator,
      },
      {
        id: '/gritclaw/alert-rules',
        path: '/gritclaw/alert-rules',
        label: 'Alert rules',
        Icon: AlertRulesIcon,
        paint: 'fill',
        isActive: exact('/gritclaw/alert-rules'),
        isVisible: notSpectator,
      },
    ],
  },
  {
    id: 'hardware',
    label: 'Hardware',
    Icon: HardwareGlyph,
    paint: 'stroke',
    items: [
      {
        id: '/devices',
        path: '/devices',
        label: 'Devices',
        Icon: DevicesIcon,
        paint: 'stroke',
        // Highlight on the list and any /devices/:id detail page.
        isActive: (p) => p === '/devices' || p.startsWith('/devices/'),
        isVisible: notSpectator,
      },
      {
        id: '/rtl/tolerance-Profiles',
        path: '/rtl/tolerance-Profiles',
        label: 'Device Profiles',
        Icon: ToleranceIcon,
        paint: 'fill',
        isActive: exact('/rtl/tolerance-Profiles'),
        isVisible: notSpectator,
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    Icon: AdminGlyph,
    paint: 'fill',
    items: [
      {
        id: '/users',
        path: '/users',
        label: 'Users',
        Icon: UsersIcon,
        paint: 'fill',
        isActive: exact('/users'),
        isVisible: notSpectator,
      },
      {
        id: '/settings',
        path: '/settings',
        label: 'Settings',
        Icon: SettingsIcon,
        paint: 'fill',
        isActive: exact('/settings'),
        // Settings is hidden for READ_ONLY roles and SPECTATOR scope.
        isVisible: (ctx) => !ctx.isSpectator && !ctx.isReadOnly,
      },
    ],
  },
];

/** Admin role rail: same SideNav shell, admin-specific routes. */
export const ADMIN_NAV_GROUPS: NavGroup[] = [
  {
    id: 'admin-directory',
    label: 'Directory',
    Icon: AdminGlyph,
    paint: 'fill',
    items: [
      {
        id: '/admin/clients',
        path: '/admin/clients',
        label: 'Clients',
        Icon: UsersIcon,
        paint: 'fill',
        isActive: exact('/admin/clients'),
        isVisible: always,
      },
      {
        id: '/admin/resellers',
        path: '/admin/resellers',
        label: 'Resellers',
        Icon: ResellersIcon,
        paint: 'fill',
        isActive: exact('/admin/resellers'),
        isVisible: always,
      },
    ],
  },
  {
    id: 'admin-operations',
    label: 'Operations',
    Icon: HardwareGlyph,
    paint: 'stroke',
    items: [
      {
        id: '/admin/devices',
        path: '/admin/devices',
        label: 'Devices',
        Icon: DevicesIcon,
        paint: 'stroke',
        // RTLs live under Devices now; keep the item lit on RTL detail pages,
        // which are still reached from the Devices row actions.
        isActive: (p) =>
          p === '/admin/devices' ||
          p === '/admin/rtl' ||
          p.startsWith('/admin/rtl/') ||
          p.startsWith('/admin/rtl-'),
        isVisible: always,
      },
      {
        id: '/admin/gritclaw-usage',
        path: '/admin/gritclaw-usage',
        label: 'GritClaw Usage',
        Icon: AssistantIcon,
        paint: 'stroke',
        isActive: exact('/admin/gritclaw-usage'),
        isVisible: always,
      },
      {
        id: '/admin/onomondo',
        path: '/admin/onomondo',
        label: 'SIM Connectivity',
        Icon: HealthIcon,
        paint: 'fill',
        isActive: exact('/admin/onomondo'),
        isVisible: always,
      },
      {
        id: '/admin/settings',
        path: '/admin/settings',
        label: 'Settings',
        Icon: SettingsIcon,
        paint: 'fill',
        isActive: exact('/admin/settings'),
        isVisible: always,
      },
    ],
  },
];

/** Reseller role rail: same SideNav shell, reseller-specific routes. */
export const RESELLER_NAV_GROUPS: NavGroup[] = [
  {
    id: 'reseller-directory',
    label: 'Directory',
    Icon: AdminGlyph,
    paint: 'fill',
    items: [
      {
        id: '/reseller/clients',
        path: '/reseller/clients',
        label: 'Clients',
        Icon: UsersIcon,
        paint: 'fill',
        isActive: exact('/reseller/clients'),
        isVisible: always,
      },
      {
        id: '/reseller/resellers',
        path: '/reseller/resellers',
        label: 'Resellers',
        Icon: ResellersIcon,
        paint: 'fill',
        isActive: exact('/reseller/resellers'),
        isVisible: always,
      },
      {
        id: '/reseller/users',
        path: '/reseller/users',
        label: 'Users',
        Icon: UsersIcon,
        paint: 'fill',
        isActive: exact('/reseller/users'),
        isVisible: always,
      },
    ],
  },
  {
    id: 'reseller-operations',
    label: 'Operations',
    Icon: HardwareGlyph,
    paint: 'stroke',
    items: [
      {
        id: '/reseller/rtl',
        path: '/reseller/rtl',
        label: 'RTL',
        Icon: RtlIcon,
        paint: 'fill',
        isActive: (p) =>
          p === '/reseller/rtl' || p.startsWith('/reseller/rtl/'),
        isVisible: always,
      },
      {
        id: '/reseller/settings',
        path: '/reseller/settings',
        label: 'Settings',
        Icon: SettingsIcon,
        paint: 'fill',
        isActive: exact('/reseller/settings'),
        isVisible: always,
      },
    ],
  },
];

/** Drop items the current role can't see, then drop any now-empty sections. */
export const resolveGroups = (
  groups: NavGroup[],
  ctx: NavContext
): NavGroup[] =>
  groups
    .map((g) => ({ ...g, items: g.items.filter((it) => it.isVisible(ctx)) }))
    .filter((g) => g.items.length > 0);

/** The id of the item matching `pathname`, or null if none. */
export const activeItemId = (
  groups: NavGroup[],
  pathname: string
): string | null => {
  for (const g of groups) {
    for (const it of g.items) {
      if (it.isActive(pathname)) return it.id;
    }
  }
  return null;
};
